"use client";

import { IconCircleCheck } from "central-icons/IconCircleCheck";
import { IconExclamationCircle } from "central-icons/IconExclamationCircle";
import { useEffect, useId, useRef, useState } from "react";
import DemoWrapper from "@/components/preview/demo-wrapper";
import { Button } from "@/components/ui/button";
import { PendingLabel } from "@/components/ui/pending-label";
import DemoInput from "../demo-input";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function Field({
  autoComplete,
  error,
  label,
  onChange,
  placeholder,
  ref,
  type,
  value,
}: {
  autoComplete: string;
  error?: string;
  label: string;
  onChange: (value: string) => void;
  placeholder: string;
  ref: React.Ref<HTMLInputElement>;
  type: "email" | "text";
  value: string;
}) {
  const inputId = useId();
  const errorId = useId();

  return (
    <div className="flex w-full flex-col gap-1.5">
      <label className="text-gray-1000 text-sm" htmlFor={inputId}>
        {label}
      </label>
      <DemoInput
        aria-describedby={error ? errorId : undefined}
        aria-invalid={error ? true : undefined}
        autoComplete={autoComplete}
        id={inputId}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        ref={ref}
        type={type}
        value={value}
      />
      <p
        className="flex min-h-5 items-start gap-1 text-pretty text-red-500 text-sm leading-5"
        id={errorId}
      >
        {error && (
          <>
            <span className="flex size-5 items-center justify-center">
              <IconExclamationCircle className="size-4 shrink-0" />
            </span>
            {error}
          </>
        )}
      </p>
    </div>
  );
}

export default function AccessibilitySubmitValidationDemo() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState<{ email?: string; name?: string }>({});
  const [status, setStatus] = useState<"idle" | "submitting" | "done">("idle");
  const nameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (status !== "submitting") {
      return;
    }
    const timer = setTimeout(() => setStatus("done"), 900);
    return () => clearTimeout(timer);
  }, [status]);

  const handleSubmit = (event: React.SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    setStatus("idle");

    const next: { email?: string; name?: string } = {};
    if (!name.trim()) {
      next.name = "Enter your name";
    }
    if (!EMAIL_PATTERN.test(email)) {
      next.email = "Enter a valid email address";
    }
    setErrors(next);

    if (next.name) {
      nameRef.current?.focus();
      return;
    }
    if (next.email) {
      emailRef.current?.focus();
      return;
    }

    setStatus("submitting");
  };

  return (
    <DemoWrapper className="px-6 py-8" margin="">
      <form
        className="flex w-full max-w-xs flex-col"
        noValidate
        onSubmit={handleSubmit}
      >
        <Field
          autoComplete="name"
          error={errors.name}
          label="Name"
          onChange={(value) => {
            setName(value);
            setErrors((current) => ({ ...current, name: undefined }));
          }}
          placeholder="Jane Doe"
          ref={nameRef}
          type="text"
          value={name}
        />
        <Field
          autoComplete="email"
          error={errors.email}
          label="Email address"
          onChange={(value) => {
            setEmail(value);
            setErrors((current) => ({ ...current, email: undefined }));
          }}
          placeholder="jane@example.com"
          ref={emailRef}
          type="email"
          value={email}
        />
        <Button
          className="mt-2 overflow-hidden"
          disabled={status === "submitting"}
          rounded
          size="md"
          type="submit"
          variant="special"
        >
          <PendingLabel
            pending={status === "submitting"}
            spinnerClassName="text-white"
          >
            Create account
          </PendingLabel>
        </Button>
        <p
          className="mt-2 flex min-h-5 items-center justify-center gap-1.5 text-gray-1100 text-sm"
          role="status"
        >
          {status === "done" && (
            <>
              <IconCircleCheck className="size-4 shrink-0 text-sky-500" />
              Account created
            </>
          )}
        </p>
      </form>
    </DemoWrapper>
  );
}
